import ConnectBar from '@econsult/econsult-ui/dist/components/ConnectBar';
import { Notification } from '@econsult/econsult-ui/dist/components/Notification';
import { AppErrorFallback } from 'components/App/Error';
import PatientInfo from 'components/Consent/PatientInfo';
import Page from 'components/Page';
import LiveTranscription from 'components/Transcription/LiveTranscription';
import { SAVING } from 'model/toolbar/saveToRecord';
import { useEffect, useState } from 'react';
import { triggerToolbarGetCurrentPatient } from 'store/action/toolbar/getCurrentPatient';
import { getWallet } from 'store/action/wallet';
import { useAppDispatch, useAppState } from 'store/configureStore';
import { hasUnlimitedSub } from 'store/reducer/wallet';
import styled from 'styled-components';
import theme from 'styles/theme';
import { getButtonProps, getPatientMatchingChip, getSaveToRecordState } from 'util/helper';
import ConsultationHeading from './ConsultationHeading';

const WarningContainer = styled.div`
  background: #D7F4FE;
  border-radius: 8px;
  padding: 8px;
  margin-bottom: 16px;
`;

const WarningText = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #1E194B;
  margin: 0;
`;

export interface NotificationModel {
  textContent: string;
  state: 'info' | 'warning' | 'urgent';
}

export default function Consultation() {
  const dispatch = useAppDispatch();
  const {
    summary: { currentSummary },
    app: {
      clinicalSystemName,
      roles,
      selectedPractice,
      user: { summariserIds },
    },
    toolbar: { patient, saveToRecordStatus },
    wallet: { topup, subscription },
    featureFlags,
  } = useAppState();
  const [notification, setNotification] = useState<NotificationModel | null>(null);
  const [blockUserCheck, setBlockUserCheck] = useState(false);
  const clearNotification = () => {
    setNotification(null);
  };
  const { isInteropEnabled, isPatientMatched, patientMatchingChip } = getPatientMatchingChip(
    patient,
    clinicalSystemName,
  );
  const saveToRecordState = getSaveToRecordState(saveToRecordStatus);
  const showRightContainerPart = Boolean(currentSummary?.length) && saveToRecordStatus !== SAVING;

  useEffect(() => {
    if (patient === null) {
      if (featureFlags.SUMMARISER_EMIS_INTEGRATION_ENABLED && roles.includes('TOOLBAR')) {
        triggerToolbarGetCurrentPatient(dispatch);
      }
    }
  }, [patient, dispatch, featureFlags, roles]);

  useEffect(() => {
    // remove later once default true > TEMP_SUMMARISER_PAYMENT_ENABLED
    if (selectedPractice?.id) {
      if (featureFlags.TEMP_SUMMARISER_PAYMENT_ENABLED || summariserIds?.includes(selectedPractice?.id)) {
        getWallet(dispatch, selectedPractice?.id);
        setBlockUserCheck(false);
      } else {
        setBlockUserCheck(true);
      }
    }
    return () => {};
  }, [dispatch, selectedPractice, summariserIds, featureFlags.TEMP_SUMMARISER_PAYMENT_ENABLED]);

  if (blockUserCheck) {
    return (
      <Page>
        <AppErrorFallback />
      </Page>
    );
  }

  return (
    <Page>
      {notification ? (
        <Notification
          textContent={notification.textContent}
          state={notification.state}
          dismissable
          onDismiss={clearNotification}
        />
      ) : null}
      <ConsultationHeading />
      <PatientInfo />
      {!hasUnlimitedSub(subscription) &&
        topup &&
        topup.totalUsableSeconds !== null &&
        topup.totalUsableSeconds < 600 &&
        topup.totalUsableSeconds > 0 && (
          <WarningContainer style={{ margin: 0, marginBottom: '16px', backgroundColor: theme.colorStatusImportantBg }}>
            <WarningText>
              You have less than 10 minutes remaining today. The transcriber will stop automatically when your time runs
              out.
            </WarningText>
          </WarningContainer>
        )}
      {isInteropEnabled && (
        <ConnectBar
          patientMatchingChip={patientMatchingChip}
          onClickReload={() => triggerToolbarGetCurrentPatient(dispatch)}
          handleOnClickSaveAction={() => {}}
          handleOnClickStartAgainAction={() => {}}
          isInteropEnabled={isInteropEnabled}
          showRightContainer={isInteropEnabled && showRightContainerPart}
          isSearchingPatient={false}
          buttonProps={getButtonProps(
            isPatientMatched,
            saveToRecordState,
            patientMatchingChip.matchState,
            patientMatchingChip.matchData,
          )}
          isSaveButtonDisabled
        />
      )}
      <LiveTranscription setNotification={setNotification} notification={notification} />
    </Page>
  );
}
